import {
  HtmlFinalStep,
  HtmlStepperField,
  HtmlStepperStep,
} from '@superfunnel/types';
import Badge from '@superfunnel/ui-core/v2/Badge';
import Button from '@superfunnel/ui-core/v2/Button';
import CodeInput from '@superfunnel/ui-core/v2/CodeInput';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@superfunnel/ui-core/v2/popover';
import { PopoverPanelHeader } from '@superfunnel/ui-core/v2/PopoverPanel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import { classNames } from '@superfunnel/utils';
import { EditPencil } from 'iconoir-react';
import React, { ReactNode } from 'react';
import { StepConditionEditor } from '../StepConditionEditor';
import { useHtmlStepperEditor } from './HtmlStepperEditorContext';
import { FinalStepThumbnail, StepThumbnail } from './StepThumbnail';

// Field type display labels
const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  email: 'Email',
  tel: 'Phone',
  number: 'Number',
  textarea: 'Text Area',
  date: 'Date',
  country: 'Country',
  state: 'State',
  phone: 'Phone',
  select: 'Select',
  multiChoice: 'Multi-Choice',
  checkbox: 'Checkbox',
  range: 'Range',
  hidden: 'Hidden',
};

interface StepEditPopoverProps {
  /** The trigger element that opens the popover */
  children: ReactNode;
  /** Whether the popover is open */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** The type of content to show: 'step', 'finalStep', or 'wrapper' */
  type: 'step' | 'finalStep' | 'wrapper';
  /** The step data (for type='step') */
  step?: HtmlStepperStep;
  /** The final step data (for type='finalStep') */
  finalStep?: HtmlFinalStep;
  /** Alignment of the popover */
  align?: 'start' | 'center' | 'end';
  /** Side of the popover */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Side offset of the popover */
  sideOffset?: number;
  /** Align offset of the popover */
  alignOffset?: number;
}

/**
 * Popover component for editing step/finalStep/wrapper content.
 * Shows ContentTab in editor/preview mode, ConditionsTab in logic mode.
 */
export const StepEditPopover = ({
  children,
  open,
  onOpenChange,
  type,
  step,
  finalStep,
  align = 'start',
  side = 'right',
  sideOffset = 8,
  alignOffset = 0,
}: StepEditPopoverProps) => {
  const { state, data } = useHtmlStepperEditor();
  const { editorMode } = state;
  const { formData } = data;

  const isWrapperSelected = type === 'wrapper';

  // Get title based on type
  const getTitle = () => {
    if (isWrapperSelected) return 'Wrapper';
    if (step) {
      const stepIndex = formData?.steps.findIndex((s) => s.id === step.id) ?? 0;
      return step.name || `Step ${stepIndex + 1}`;
    }
    if (finalStep) {
      const finalStepIndex =
        formData?.finalSteps.findIndex((s) => s.id === finalStep.id) ?? 0;
      return finalStep.name || `Final Step ${finalStepIndex + 1}`;
    }
    return 'Edit';
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange} modal>
      <PopoverAnchor asChild>{children}</PopoverAnchor>
      <PopoverContent
        align={align}
        side={side}
        className="nowheel nopan nodrag max-h-[70vh] w-80 cursor-default overflow-y-auto p-0"
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        collisionPadding={16}
        style={{ pointerEvents: 'auto' }}
        onInteractOutside={(e: React.MouseEvent | CustomEvent) => {
          // Check if click is inside the anchor (step card or react-flow node)
          // If so, prevent default - the node click handler will handle toggling
          const target = e.target as HTMLElement;
          if (
            target.closest('[data-step-card]') ||
            target.closest('.react-flow__node')
          ) {
            e.preventDefault();
          }
        }}
      >
        {/* Header */}

        <PopoverPanelHeader className="border-base-100 border-b px-4 py-2">
          <h3 className="text-base-900 text-sm font-medium">{getTitle()}</h3>
        </PopoverPanelHeader>

        {/* Content */}
        <div className="p-4">
          {editorMode !== 'logic' ? (
            <StepEditContent
              type={type}
              step={step}
              finalStep={finalStep}
              wrapper={formData?.wrapper}
            />
          ) : (
            <StepConditionsContent step={step} finalStep={finalStep} />
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

interface StepEditContentProps {
  type: 'step' | 'finalStep' | 'wrapper';
  step?: HtmlStepperStep;
  finalStep?: HtmlFinalStep;
  wrapper?: string;
}

/**
 * Content for editing step/finalStep/wrapper HTML and fields.
 */
const StepEditContent = ({
  type,
  step,
  finalStep,
  wrapper,
}: StepEditContentProps) => {
  const { actions, modals } = useHtmlStepperEditor();
  const { updateStep, updateFinalStep, updateWrapper } = actions;
  const { openEditFieldModal } = modals;

  if (type === 'wrapper') {
    return (
      <div className="flex flex-col gap-4">
        <div className="text-base-500 text-sm">
          The wrapper HTML wraps around all steps. It must contain an element
          with <code className="bg-base-100 rounded px-1">data-sf-steps</code>{' '}
          attribute where the steps will be rendered.
        </div>
        <CodeInput
          title="Wrapper HTML"
          titleClassName="text-base-700 flex items-center gap-2 text-sm font-medium"
          value={wrapper ?? ''}
          onChange={(code) => {
            updateWrapper(code);
          }}
          language="html"
          inlineLines={10}
          placeholder='<div class="wrapper">\n  <div data-sf-steps></div>\n</div>'
        />
      </div>
    );
  }

  if (type === 'step' && step) {
    const fields = step.fields ?? [];

    return (
      <div className="flex flex-col gap-4">
        {/* Fields List */}
        <div className="flex flex-col gap-2">
          <FieldTitle title="Fields" />
          {fields.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              {fields.map((field) => (
                <FieldListItem
                  key={field.id}
                  field={field}
                  stepId={step.id}
                  onEdit={openEditFieldModal}
                />
              ))}
            </div>
          ) : (
            <div className="text-base-400 py-2 text-center text-sm">
              No fields in this step
            </div>
          )}
        </div>
        <CodeInput
          title="HTML Content"
          titleClassName="text-base-700 flex items-center gap-2 text-sm font-medium"
          value={step.html ?? ''}
          onChange={(code) => {
            updateStep(step.id, { html: code });
          }}
          readOnly
          language="html"
          inlineLines={5}
          placeholder="<!-- Add your custom code here -->"
        />
      </div>
    );
  }

  if (type === 'finalStep' && finalStep) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <FieldTitle title="Step Type" />
          <Select
            value={finalStep.stepType}
            onValueChange={(value) =>
              updateFinalStep(finalStep.id, {
                stepType: value as 'redirect' | 'html',
              })
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select step type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="html">HTML</SelectItem>
              <SelectItem value="redirect">Redirect</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {finalStep.stepType === 'redirect' && (
          <div>
            <Input
              type="text"
              title="Redirect URL"
              value={finalStep.url}
              onChange={(e) =>
                updateFinalStep(finalStep.id, { url: e.target.value })
              }
              placeholder="https://example.com"
            />
          </div>
        )}

        {finalStep.stepType === 'html' && (
          <CodeInput
            title="HTML Content"
            titleClassName="text-base-700 flex items-center gap-2 text-sm font-medium"
            value={finalStep.html ?? ''}
            onChange={(code) => {
              updateFinalStep(finalStep.id, { html: code });
            }}
            readOnly
            language="html"
            inlineLines={5}
            placeholder="<!-- Add your custom code here -->"
          />
        )}
      </div>
    );
  }

  return <div className="p-4 text-center text-gray-500">No step selected</div>;
};

interface StepConditionsContentProps {
  step?: HtmlStepperStep;
  finalStep?: HtmlFinalStep;
}

/**
 * Content for editing step conditions (logic mode).
 */
const StepConditionsContent = ({ step }: StepConditionsContentProps) => {
  const { data, actions } = useHtmlStepperEditor();
  const { formData } = data;
  const { updateStep } = actions;

  if (!step) {
    return (
      <div className="p-4 text-center text-gray-500">
        Conditions are only available for regular steps
      </div>
    );
  }

  const allSteps = formData?.steps ?? [];
  const finalSteps = formData?.finalSteps ?? [];
  const currentStepIndex = allSteps.findIndex((s) => s.id === step.id);

  return (
    <StepConditionEditor
      currentStep={step}
      allSteps={allSteps}
      finalSteps={finalSteps}
      currentStepIndex={currentStepIndex}
      onConditionsChange={(conditions) => {
        updateStep(step.id, { conditions });
      }}
      defaultNextStep={step.defaultNextStep}
      onDefaultNextStepChange={(stepId) => {
        updateStep(step.id, { defaultNextStep: stepId || undefined });
      }}
      getStepDisplayName={(s) => s?.name || 'Step'}
      renderStepThumbnail={(stepId, isFinalStep) => {
        if (isFinalStep) {
          const finalStep = formData?.finalSteps.find((s) => s.id === stepId);
          if (finalStep) {
            return <FinalStepThumbnail step={finalStep} height={140} />;
          }
        }
        return <StepThumbnail stepId={stepId} height={140} />;
      }}
    />
  );
};

// Field list item component
interface FieldListItemProps {
  field: HtmlStepperField;
  stepId: string;
  onEdit: (stepId: string, field: HtmlStepperField) => void;
}

const FieldListItem = ({ field, stepId, onEdit }: FieldListItemProps) => {
  const typeLabel = FIELD_TYPE_LABELS[field.type] || field.type;

  return (
    <div
      className={classNames(
        'border-base-100 hover:bg-base-50 flex items-center justify-between rounded-lg border px-3 py-2',
        'cursor-pointer transition-colors',
      )}
      onClick={() => onEdit(stepId, field)}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <p>
          <span className="text-base-900 truncate text-sm font-medium">
            {field.id}
          </span>
          {field.required && (
            <span className="text-red-500" title="Required">
              *
            </span>
          )}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Badge variant="secondary" className="text-xs">
          {typeLabel}
        </Badge>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(stepId, field);
          }}
        >
          <EditPencil className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};
